import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { ref, onValue, update, remove, get } from 'firebase/database';
import { Search, Heart, Star, Trash2, QrCode, X, MapPin, Navigation, Compass, ChevronLeft, ChevronRight, Bot, Split, ShoppingBag, CalendarDays, CreditCard, Map as MapIcon, List as ListIcon, Calendar, Wallet, Tag, MessageSquare, User, Clock, Copy, CheckCircle, HelpCircle } from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';
import { format } from 'date-fns';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import PropertyDetails from './PropertyDetails';
import BookingModal from './BookingModal';
import RescheduleModal from './RescheduleModal';
import RescheduleRoomModal from './RescheduleRoomModal';
import RefundModal from './RefundModal';
import ReviewModal from './ReviewModal';
import AiChatBot from './AiChatBot';
import Chat from './Chat';
import BillSplitterModal from './BillSplitterModal';

import QrScanner from './QrScanner';
import TermsAndPolicies from './TermsAndPolicies';
import SystemTutorialModal from './SystemTutorialModal';
import { ReviewPhotoViewer, CompactReviewCard } from './ReviewPhotoViewer';

const ChatRoomItem = ({ room, onClick }) => {
  const [photo, setPhoto] = useState(room.otherProfilePic || null);

  useEffect(() => {
    if (room.otherProfilePic) return;

    const fetchPhoto = async () => {
      try {
        const propSnap = await get(ref(db, `properties/${room.otherUid}`));
        if (propSnap.exists()) {
          const data = propSnap.val();
          const imgs = Array.isArray(data.imageUrls) ? data.imageUrls : (data.imageUrls ? Object.values(data.imageUrls) : []);
          if (imgs.length > 0) setPhoto(imgs[0]);
        } else {
          const userSnap = await get(ref(db, `users/${room.otherUid}`));
          if (userSnap.exists() && userSnap.val().profilePicUrl) {
            setPhoto(userSnap.val().profilePicUrl);
          }
        }
      } catch (e) {
        console.error("Chat photo fetch error", e);
      }
    };
    fetchPhoto();
  }, [room.otherUid, room.otherProfilePic]);

  return (
    <div
      className="card chat-room-card"
      style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '16px', padding: '16px', transition: 'var(--transition)' }}
      onClick={() => onClick(room)}
    >
      <div style={{
        width: '52px', height: '52px', borderRadius: '18px',
        background: 'var(--light-bg)', overflow: 'hidden',
        display: 'flex', justifyContent: 'center', alignItems: 'center', color: 'var(--text-muted)'
      }}>
        {photo ? (
          <img src={photo} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <User size={28} />
        )}
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <h4 style={{ margin: 0, fontWeight: 800 }}>{room.otherUserName}</h4>
          <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>{room.timestamp ? format(new Date(room.timestamp), 'p') : ''}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '4px' }}>
          <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            View messages
          </p>
          {room.unreadCount > 0 && (
            <span style={{ background: 'var(--primary)', color: 'white', fontSize: '11px', fontWeight: 800, padding: '2px 8px', borderRadius: '12px' }}>
              {room.unreadCount}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

const TouristDashboard = ({ profile, uid, onViewPolicies, onEditProfile }) => {
  const [activeTab, setActiveTab] = useState(() => sessionStorage.getItem('td_activeTab') || 'Partners');
  const [searchQuery, setSearchQuery] = useState('');
  const [properties, setProperties] = useState([]);
  const [favorites, setFavorites] = useState({});
  const [myBookings, setMyBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPropertyId, setSelectedPropertyId] = useState(() => sessionStorage.getItem('td_selectedPropertyId') || null);
  const [bookingRoom, setBookingRoom] = useState(() => {
    const saved = sessionStorage.getItem('td_bookingRoom');
    if (saved) {
      try { return JSON.parse(saved); } catch(e) {}
    }
    return null;
  });
  const [selectedChat, setSelectedChat] = useState(null);
  const [reviewBooking, setReviewBooking] = useState(null);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [rescheduleBooking, setRescheduleBooking] = useState(null);
  const [refundBooking, setRefundBooking] = useState(null);
  const [showAiBot, setShowAiBot] = useState(false);
  const [confirmCancelId, setConfirmCancelId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [propertyLimit, setPropertyLimit] = useState(6);
  const [bookingLimit, setBookingLimit] = useState(5);
  const [detailBooking, setDetailBooking] = useState(null);
  const [billSplitterBooking, setBillSplitterBooking] = useState(null);
  const [showBreakdownBooking, setShowBreakdownBooking] = useState(null);

  const [showGlobalSplitter, setShowGlobalSplitter] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [scannedBillData, setScannedBillData] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'map'
  const [showTerms, setShowTerms] = useState(false);
  const [expenseMonthFilter, setExpenseMonthFilter] = useState('All');
  const [expenseStatusFilter, setExpenseStatusFilter] = useState('All');
  const [showGcashPrompt, setShowGcashPrompt] = useState(false);
  const [gcashPromptReason, setGcashPromptReason] = useState('refund'); // 'refund' or 'first_time'
  const [showTutorial, setShowTutorial] = useState(false);
  const [userCoupons, setUserCoupons] = useState([]);
  const [promoEvents, setPromoEvents] = useState([]);
  const [copiedPromoCode, setCopiedPromoCode] = useState(null);
  const [chatRooms, setChatRooms] = useState([]);
  const [recentReviews, setRecentReviews] = useState([]);
  const [viewerPhotos, setViewerPhotos] = useState(null);
  const [viewerIndex, setViewerIndex] = useState(0);

  // Check if tourist account needs the introductory tutorial tour
  useEffect(() => {
    if (!profile || !uid) return;

    const tutorialSeenLocal = localStorage.getItem(`tutorial_seen_${uid}`) === 'true' || sessionStorage.getItem(`tutorial_seen_${uid}`) === 'true';
    const tutorialSeenDb = profile.hasSeenTutorial === true;

    // Trigger tutorial if not yet seen
    if (!tutorialSeenLocal && !tutorialSeenDb) {
      setShowTutorial(true);
    } else if (tutorialSeenDb && !tutorialSeenLocal) {
      localStorage.setItem(`tutorial_seen_${uid}`, 'true');
    }

    // Only prompt for profile setup if tutorial is not actively displaying
    const hasPhone = profile?.phoneNumber || profile?.phone;
    const hasProfileSetup = hasPhone && profile?.gcashNumber && profile?.gcashName;

    if (!hasProfileSetup && (tutorialSeenLocal || tutorialSeenDb)) {
      const prompted = sessionStorage.getItem(`profile_setup_prompted_${uid}`);
      if (!prompted) {
        sessionStorage.setItem(`profile_setup_prompted_${uid}`, 'true');
        setGcashPromptReason('first_time');
        setShowGcashPrompt(true);
      }
    }
  }, [profile, uid]);

  const handleRequestRefund = (b) => {
    if (!profile?.gcashName || !profile?.gcashNumber) {
      setGcashPromptReason('refund');
      setShowGcashPrompt(true);
      return;
    }
    setRefundBooking(b);
  };

  const handleBookAgain = (b) => {
    // Navigate to property details
    setActiveTab('Partners');
    setSelectedPropertyId(b.ownerUid || b.propertyUid);
  };

  useEffect(() => {
    sessionStorage.setItem('td_activeTab', activeTab);
  }, [activeTab]);

  useEffect(() => {
    if (selectedPropertyId) sessionStorage.setItem('td_selectedPropertyId', selectedPropertyId);
    else sessionStorage.removeItem('td_selectedPropertyId');
  }, [selectedPropertyId]);

  useEffect(() => {
    if (bookingRoom) sessionStorage.setItem('td_bookingRoom', JSON.stringify(bookingRoom));
    else sessionStorage.removeItem('td_bookingRoom');
  }, [bookingRoom]);

  useEffect(() => {
    const propsRef = ref(db, 'properties');
    const unsubscribeProps = onValue(propsRef, (snap) => {
      const data = snap.val();
      if (data) {
        const list = Object.entries(data).map(([id, val]) => ({
          ...val,
          id, // Preserves the Firebase DB key (e.g. 'otiTL85PGUhhBSsv0JonFgvWIK73')
          propertySlug: val.id || id,
          uid: val.ownerUid || val.uid || id,
          ownerUid: val.ownerUid || val.uid || id
        })).sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
        setProperties(list);
      } else {
        setProperties([]);
      }
      setLoading(false);
    }, (error) => {
      console.error("Firebase fetch error:", error);
    });

    const favsRef = ref(db, `users/${uid}/favorites`);
    const unsubscribeFavs = onValue(favsRef, (snap) => {
      setFavorites(snap.val() || {});
    });

    const bookingsRef = ref(db, 'bookings');
    const unsubscribeBookings = onValue(bookingsRef, (snap) => {
      const data = snap.val();
      const list = data ? Object.entries(data)
        .map(([id, val]) => ({ id, ...val }))
        .filter(b => b.touristUid === uid)
        .sort((a, b) => {
          const aTime = (typeof a.timestamp === 'number') ? a.timestamp : (a.timestamp && typeof a.timestamp === 'object' ? Date.now() : 0);
          const bTime = (typeof b.timestamp === 'number') ? b.timestamp : (b.timestamp && typeof b.timestamp === 'object' ? Date.now() : 0);
          return bTime - aTime;
        }) : [];

      // Check if any booking is expired without check-in
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      list.forEach(async (b) => {
        const rawStatus = (b.status || '').trim().toLowerCase();
        if (rawStatus === 'confirmed' || rawStatus === 'pending') {
          const dateStr = b.bookingDate || b.checkInDate || b.date;
          if (dateStr) {
            try {
              const bDate = new Date(dateStr);
              if (!isNaN(bDate.getTime())) {
                bDate.setHours(0, 0, 0, 0);
                const nights = parseInt(b.nights) || 1;
                const endDate = new Date(bDate);
                endDate.setDate(endDate.getDate() + nights);
                endDate.setHours(0, 0, 0, 0);

                if (today > endDate) {
                  await update(ref(db, `bookings/${b.id}`), {
                    status: 'No Show',
                    cancellationReason: 'Did not check in and checkout date has passed.'
                  });
                }
              }
            } catch (err) {}
          }
        }
      });

      setMyBookings(list);
    });

    const userCouponsRef = ref(db, `user_coupons/${uid}`);
    const unsubscribeUserCoupons = onValue(userCouponsRef, (snap) => {
      const data = snap.val();
      const list = data ? Object.entries(data).map(([code, val]) => ({ code, ...val })) : [];
      setUserCoupons(list);
    });

    const promosRef = ref(db, 'cms/homepage/promotions');
    const unsubscribePromos = onValue(promosRef, (snap) => {
      const data = snap.val();
      const list = data ? Object.entries(data).map(([id, val]) => ({ id, ...val })) : [];
      setPromoEvents(list);
    });

    const chatRoomsRef = ref(db, `chat_rooms/${uid}`);
    const unsubscribeChats = onValue(chatRoomsRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        const list = Object.entries(data).map(([otherUid, val]) => ({
          otherUid,
          ...val
        })).sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
        setChatRooms(list);
      } else {
        setChatRooms([]);
      }
    });

    const reviewsRef = ref(db, 'reviews');
    const unsubscribeReviews = onValue(reviewsRef, (snap) => {
      const data = snap.val();
      if (data && typeof data === 'object') {
        let allRevs = [];
        Object.entries(data).forEach(([ownerUid, reviews]) => {
          if (reviews && typeof reviews === 'object') {
            Object.entries(reviews).forEach(([reviewId, r]) => {
              if (r && typeof r === 'object') {
                allRevs.push({ id: reviewId, ...r, ownerUid });
              }
            });
          }
        });
        allRevs.sort((a, b) => {
          const tA = typeof a.timestamp === 'number' ? a.timestamp : (typeof a.createdAt === 'number' ? a.createdAt : 0);
          const tB = typeof b.timestamp === 'number' ? b.timestamp : (typeof b.createdAt === 'number' ? b.createdAt : 0);
          return tB - tA;
        });
        setRecentReviews(allRevs.slice(0, 10));
      } else {
        setRecentReviews([]);
      }
    });

    return () => {
      unsubscribeProps();
      unsubscribeFavs();
      unsubscribeBookings();
      unsubscribeUserCoupons();
      unsubscribePromos();
      unsubscribeChats();
      unsubscribeReviews();
    };
  }, [uid]);

  useEffect(() => {
    const handleOpenChat = (e) => {
      if (e.detail?.otherUid) {
        setSelectedChat({
          id: e.detail.otherUid,
          name: e.detail.otherUserName || 'User'
        });
      }
    };
    window.addEventListener('rc-open-chat', handleOpenChat);
    return () => window.removeEventListener('rc-open-chat', handleOpenChat);
  }, []);

  const toggleFavorite = async (e, propId) => {
    e.stopPropagation();
    const isFav = favorites[propId];
    if (isFav) {
      await remove(ref(db, `users/${uid}/favorites/${propId}`));
    } else {
      await update(ref(db, `users/${uid}/favorites`), { [propId]: true });
    }
  };

  if (selectedChat) return <Chat currentUid={uid} otherUserUid={selectedChat.id} otherUserName={selectedChat.name} onBack={() => setSelectedChat(null)} />;

  if (selectedPropertyId) {
    const prop = properties.find(p => p.id === selectedPropertyId);

    return (
      <div className="view-transition">
        <PropertyDetails
          propId={selectedPropertyId}
          propertyData={prop}
          user={profile}
          onBack={() => setSelectedPropertyId(null)}
          onBookRoom={(room) => setBookingRoom({ room, property: prop })}
          onChat={(p) => setSelectedChat({ id: p.ownerUid || p.uid || p.id || selectedPropertyId, name: p.name })}
          onViewPolicies={onViewPolicies}
        />
        {bookingRoom && <BookingModal room={bookingRoom.room} property={bookingRoom.property} user={profile} onClose={() => setBookingRoom(null)} onViewPolicies={onViewPolicies} />}

        <footer style={{ marginTop: '40px', paddingTop: '20px', paddingBottom: '20px', textAlign: 'center', borderTop: '1px solid var(--border)' }}>
          <button onClick={() => setShowTerms(true)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: '13px', textDecoration: 'underline', cursor: 'pointer' }}>
            Platform Terms & Policies
          </button>
        </footer>
        {showTerms && <TermsAndPolicies onClose={() => setShowTerms(false)} />}
      </div>
    );
  }

  const filteredBySearch = properties.filter(p =>
    p.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.type?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="dashboard" style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: '24px', width: '100%' }}>
        <div className="tab-container" style={{
          display: 'flex', gap: '8px',
          background: 'var(--nav-group-bg)', padding: '6px', borderRadius: '40px',
          maxWidth: '100%', overflowX: 'auto', WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'none', msOverflowStyle: 'none'
        }}>
          {['Partners', 'Favorites', 'Chat', 'My Bookings', 'My Expenses', 'My Coupons'].map(tab => {
            const isChat = tab === 'Chat';
            const totalUnread = isChat ? chatRooms.reduce((sum, room) => sum + (parseInt(room.unreadCount) || 0), 0) : 0;
            const badgeCount = isChat ? totalUnread : 0;

            const tabId = `tourist-tab-${tab.toLowerCase().replace(/\s+/g, '-')}`;

            return (
              <button
                key={tab}
                id={tabId}
                onClick={() => setActiveTab(tab)}
                style={{
                  padding: '10px 24px',
                  background: activeTab === tab ? 'var(--surface)' : 'transparent',
                  border: 'none',
                  borderRadius: '30px',
                  color: activeTab === tab ? 'var(--primary)' : 'var(--text-muted)',
                  fontWeight: 700,
                  fontSize: '14px',
                  cursor: 'pointer',
                  boxShadow: activeTab === tab ? '0 4px 12px rgba(0,0,0,0.08)' : 'none',
                  transition: 'var(--transition)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                {tab}
                {badgeCount > 0 && (
                  <span style={{
                    background: 'var(--primary)',
                    color: 'white',
                    fontSize: '11px',
                    fontWeight: 800,
                    padding: '2px 8px',
                    borderRadius: '12px'
                  }}>
                    {badgeCount}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <button
          onClick={() => setShowTutorial(true)}
          title="View System Tutorial Tour"
          style={{
            marginLeft: '12px',
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: '30px',
            padding: '10px 18px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: '13px',
            fontWeight: 700,
            color: 'var(--text-main)',
            cursor: 'pointer',
            boxShadow: 'var(--shadow)',
            transition: 'var(--transition)',
            flexShrink: 0
          }}
          onMouseOver={(e) => { e.currentTarget.style.color = 'var(--primary)'; e.currentTarget.style.borderColor = 'var(--primary)'; }}
          onMouseOut={(e) => { e.currentTarget.style.color = 'var(--text-main)'; e.currentTarget.style.borderColor = 'var(--border)'; }}
        >
          <HelpCircle size={16} color="var(--primary)" />
          <span>Tour Guide</span>
        </button>
      </div>

      {activeTab === 'Partners' && (
        <>
          <div style={{ position: 'relative', marginBottom: '32px' }}>
            <Search style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: 'var(--secondary)' }} size={20} />
            <input
              type="text"
              placeholder="Where do you want to go?"
              className="input"
              style={{ paddingLeft: '48px', height: '56px', borderRadius: '20px', fontSize: '16px', boxShadow: 'var(--shadow)' }}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Compass size={18} color="var(--primary)" />
              <h3 style={{ margin: 0, fontSize: '20px', fontWeight: 800 }}>Explore Destinations</h3>
            </div>

            {/* View Mode Toggle */}
            <div style={{ display: 'flex', background: 'rgba(0,0,0,0.05)', borderRadius: '12px', padding: '4px' }}>
              <button
                className="btn"
                style={{ padding: '8px 12px', borderRadius: '10px', background: viewMode === 'list' ? 'white' : 'transparent', color: viewMode === 'list' ? 'var(--primary)' : 'var(--text-muted)', border: 'none', boxShadow: viewMode === 'list' ? '0 2px 8px rgba(0,0,0,0.1)' : 'none', display: 'flex', alignItems: 'center', gap: '6px' }}
                onClick={() => setViewMode('list')}
              >
                <ListIcon size={16} /> <span style={{ fontSize: '13px', fontWeight: 700 }}>List</span>
              </button>
              <button
                className="btn"
                style={{ padding: '8px 12px', borderRadius: '10px', background: viewMode === 'map' ? 'white' : 'transparent', color: viewMode === 'map' ? 'var(--primary)' : 'var(--text-muted)', border: 'none', boxShadow: viewMode === 'map' ? '0 2px 8px rgba(0,0,0,0.1)' : 'none', display: 'flex', alignItems: 'center', gap: '6px' }}
                onClick={() => setViewMode('map')}
              >
                <MapIcon size={16} /> <span style={{ fontSize: '13px', fontWeight: 700 }}>Map</span>
              </button>
            </div>
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '100px 0' }}><div className="loader" style={{ margin: '0 auto' }}></div></div>
          ) : (
            <>
              {viewMode === 'map' ? (
                <div style={{ height: '600px', width: '100%', borderRadius: '24px', overflow: 'hidden', border: '1px solid var(--border)', zIndex: 0, boxShadow: 'var(--shadow)', position: 'relative' }}>
                  <MapContainer center={[12.8797, 121.7740]} zoom={6} style={{ height: '100%', width: '100%', zIndex: 0 }}>
                    <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap" />
                    {filteredBySearch.map(prop => {
                      if (prop.latitude && prop.longitude && prop.latitude !== 0) {
                        return (
                          <Marker key={prop.id} position={[prop.latitude, prop.longitude]}>
                            <Popup>
                              <div style={{ textAlign: 'center' }}>
                                <strong style={{ fontSize: '14px', display: 'block', marginBottom: '4px' }}>{prop.name}</strong>
                                <button
                                  className="btn btn-primary"
                                  style={{ padding: '6px 12px', fontSize: '12px', borderRadius: '8px', marginTop: '8px' }}
                                  onClick={() => setSelectedPropertyId(prop.id)}
                                >
                                  View Details
                                </button>
                              </div>
                            </Popup>
                          </Marker>
                        );
                      }
                      return null;
                    })}
                  </MapContainer>
                </div>
              ) : (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '24px', width: '100%' }}>
                    {filteredBySearch.slice(0, propertyLimit).map(prop => (
                      <PropertyCard key={prop.id} prop={prop} isFav={!!favorites[prop.id]} onFav={(e) => toggleFavorite(e, prop.id)} onClick={() => setSelectedPropertyId(prop.id)} />
                    ))}
                  </div>
                  {filteredBySearch.length > propertyLimit && (
                    <div style={{ textAlign: 'center', marginTop: '40px' }}>
                      <button className="btn btn-secondary" onClick={() => setPropertyLimit(prev => prev + 6)}>Load More Properties</button>
                    </div>
                  )}
                  {filteredBySearch.length === 0 && <p style={{ textAlign: 'center', gridColumn: '1/-1', color: 'var(--text-muted)', padding: '60px 0' }}>No properties found matching your search.</p>}

                  {/* ── Guest Reviews Carousel / Section ── */}
                  {recentReviews.length > 0 && (
                    <div style={{ marginTop: '56px', width: '100%' }}>
                      <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', color: '#F59E0B', marginBottom: '8px' }}>
                          <Star size={20} fill="#F59E0B" />
                          <h3 style={{ margin: 0, fontSize: '20px', fontWeight: 800, color: '#F59E0B' }}>What Our Guests Say</h3>
                        </div>
                        <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-muted)' }}>Real experiences and feedback from authentic guests</p>
                      </div>

                      <div
                        style={{
                          columnCount: 'auto',
                          columnWidth: '320px',
                          columnGap: '20px',
                          width: '100%',
                          textAlign: 'left'
                        }}
                      >
                        {recentReviews.map((rev, idx) => {
                          const propObj = properties.find(p => p.uid === rev.ownerUid || p.id === rev.ownerUid);
                          const propName = rev.propertyName || rev.resortName || propObj?.name || 'Resort Partner';
                          const enrichedReview = { ...rev, propertyName: propName };
                          return (
                            <CompactReviewCard
                              key={rev.id || idx}
                              review={enrichedReview}
                              showDate={true}
                              onPhotoClick={(photos, pIdx) => {
                                setViewerPhotos(photos);
                                setViewerIndex(pIdx);
                              }}
                            />
                          );
                        })}
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </>
      )}

      {activeTab === 'Favorites' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '24px', width: '100%' }}>
          {properties.filter(p => favorites[p.id]).length > 0 ? properties.filter(p => favorites[p.id]).map(prop => (
            <PropertyCard key={prop.id} prop={prop} isFav={true} onFav={(e) => toggleFavorite(e, prop.id)} onClick={() => setSelectedPropertyId(prop.id)} />
          )) : (
            <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: '80px 0', opacity: 0.6 }}>
              <Heart size={48} color="var(--primary)" style={{ marginBottom: '16px' }} />
              <p style={{ fontWeight: 600 }}>Your favorite resorts will appear here.</p>
            </div>
          )}
        </div>
      )}

      {activeTab === 'My Bookings' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', maxWidth: '700px', margin: '0 auto' }}>
          <div style={{ background: 'var(--surface)', padding: '24px', borderRadius: '24px', boxShadow: 'var(--shadow)', border: '1px solid var(--border)', marginBottom: '8px' }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: 800 }}>Bookings Summary</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
              <div>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase' }}>Total Bookings</div>
                <div style={{ fontSize: '24px', fontWeight: 900, color: 'var(--text-main)' }}>{myBookings.length}</div>
              </div>
              <div>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase' }}>Total Value</div>
                <div style={{ fontSize: '24px', fontWeight: 900, color: 'var(--primary)' }}>₱{myBookings.reduce((sum, b) => sum + Number(b.totalPrice || 0), 0).toLocaleString()}</div>
              </div>
              <div>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase' }}>Unpaid Balance</div>
                <div style={{ fontSize: '24px', fontWeight: 900, color: '#ff9800' }}>₱{myBookings.reduce((sum, b) => {
                  if (['Cancelled', 'Declined', 'Refunded', 'Refund Approved', 'Refund Confirmed', 'Completed'].includes(b.status)) return sum;
                  return sum + Math.max(0, Number(b.totalPrice || 0) - Number(b.amountPaid || 0));
                }, 0).toLocaleString()}</div>
              </div>
            </div>
          </div>

        <section className="view-transition">
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '24px' }}>
            <Calendar size={18} color="var(--primary)" />
            <h3 style={{ margin: 0, fontSize: '20px', fontWeight: 800 }}>My Bookings</h3>
          </div>
          {myBookings.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <button className="btn" style={{ background: 'var(--surface)', color: 'var(--text-main)', border: '1px solid var(--border)', padding: '12px 20px', width: '100%', display: 'flex', justifyContent: 'center', marginBottom: '8px' }} onClick={() => setShowScanner(true)}>
                <QrCode size={18} /> Scan Split Bill
              </button>
              {myBookings.slice(0, bookingLimit).map(b => {
                const st = (b.status || 'pending').toLowerCase().replace(/ /g, '-');
                const isActive = b.status === 'Confirmed' || b.status === 'Checked In' || b.status === 'Refund Declined';
                let isMissed = false;
                try {
                  if (b.bookingDate && b.bookingDate !== 'N/A') {
                    const parsedDate = new Date(b.bookingDate);
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    if (parsedDate < today) isMissed = true;
                  }
                } catch(e) {}
                return (
                  <div key={b.id} className="card" style={{ padding: '20px', cursor: 'pointer', transition: 'var(--transition)' }}
                    onClick={() => setDetailBooking(b)}
                    onMouseOver={e => e.currentTarget.style.transform = 'translateY(-3px)'}
                    onMouseOut={e => e.currentTarget.style.transform = 'translateY(0)'}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                          <h4 style={{ margin: 0, fontWeight: 800, fontSize: '16px' }}>{b.propertyName}</h4>
                          <span className={`status-badge status-${st}`}>{b.status}</span>
                        </div>
                        <div style={{ fontSize: '13px', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                          <MapPin size={13} /> {b.activityTitle} &bull; {b.bookingDate}
                        </div>
                        {(b.arrivalTime || b.timeSlot || b.bookingTime) && (
                          <div style={{ fontSize: '12.5px', color: 'var(--primary)', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px', fontWeight: 700 }}>
                            <Clock size={13} /> Arrival: {b.arrivalTime || b.timeSlot || b.bookingTime}
                          </div>
                        )}
                        {b.status === 'Refund Approved' && (
                          <div style={{ margin: '8px 0', padding: '8px 12px', background: 'rgba(16, 185, 129, 0.1)', border: '1px solid rgba(16, 185, 129, 0.25)', borderRadius: '10px', fontSize: '12px', color: '#047857', fontWeight: 700 }}>
                            Refund Approved! Please wait for a moment while the owner processes and sends your refund to GCash.
                          </div>
                        )}
                        {b.status === 'Refund Confirmed' && (
                          <div style={{ margin: '8px 0', padding: '8px 12px', background: 'rgba(16, 185, 129, 0.1)', border: '1px solid rgba(16, 185, 129, 0.25)', borderRadius: '10px', fontSize: '12px', color: '#047857', fontWeight: 700 }}>
                            Your refund has been confirmed by the resort.
                          </div>
                        )}
                        <div style={{ display: 'flex', gap: '16px', marginTop: '8px' }}>
                          <div>
                            <span style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', fontWeight: 600 }}>Total Price</span>
                            <span style={{ fontWeight: 800, color: 'var(--secondary)', fontSize: '15px' }}>₱{Number(b.totalPrice || 0).toLocaleString()}</span>
                          </div>
                          <div>
                            <span style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', fontWeight: 600 }}>Balance</span>
                            <span style={{ fontWeight: 800, color: '#ff9800', fontSize: '15px' }}>₱{(['Cancelled', 'Declined', 'Refunded', 'Refund Approved', 'Refund Confirmed', 'Completed'].includes(b.status) ? 0 : Math.max(0, Number(b.totalPrice || 0) - Number(b.amountPaid || 0))).toLocaleString()}</span>
                          </div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'flex-end' }} onClick={e => e.stopPropagation()}>
                        {isActive && <button className="btn btn-primary" style={{ padding: '8px 12px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px' }} onClick={() => setSelectedBooking(b)}><QrCode size={16} /> QR</button>}
                        {isActive && <button className="btn" disabled={isMissed} style={{ padding: '6px 10px', fontSize: '11px', background: isMissed ? '#f3f4f6' : '#F5F3FF', color: isMissed ? '#9ca3af' : '#7C3AED', border: isMissed ? '1px solid #e5e7eb' : '1px solid rgba(124,58,237,0.2)', display: 'flex', alignItems: 'center', gap: '5px' }} onClick={() => setBillSplitterBooking(b)}><Split size={13} /> Split Bill</button>}

                        {(b.status === 'Confirmed' || b.status === 'Pending') && (
                          <div style={{ display: 'flex', gap: '6px' }}>
                            <button className="btn" disabled={isMissed} style={{ padding: '6px 10px', fontSize: '11px', background: isMissed ? '#f3f4f6' : '#F0FDF4', color: isMissed ? '#9ca3af' : '#16A34A', border: isMissed ? '1px solid #e5e7eb' : '1px solid rgba(22,163,74,0.2)', display: 'flex', alignItems: 'center', gap: '5px' }} onClick={() => setRescheduleBooking(b)}><CalendarDays size={13} /> Reschedule</button>
                            <button className="btn" disabled={isMissed} style={{ padding: '6px 10px', fontSize: '11px', background: isMissed ? '#f3f4f6' : 'rgba(239, 68, 68, 0.08)', color: isMissed ? '#9ca3af' : '#DC2626', border: isMissed ? '1px solid #e5e7eb' : '1px solid rgba(220,38,38,0.25)', display: 'flex', alignItems: 'center', gap: '5px' }} onClick={() => handleRequestRefund(b)}><CreditCard size={13} /> Refund</button>
                          </div>
                        )}
                        {b.status === 'Reschedule Requested' && <button className="btn" style={{ padding: '6px 10px', fontSize: '11px', background: 'rgba(239, 68, 68, 0.1)', color: '#DC2626', border: '1px solid rgba(220,38,38,0.2)', display: 'flex', alignItems: 'center', gap: '5px' }} onClick={async (e) => { e.stopPropagation(); await update(ref(db, `bookings/${b.id}`), { status: 'Confirmed', requestedRescheduleDate: null, requestedRescheduleNights: null }); }}><X size={13} /> Cancel Reschedule</button>}
                        {b.status === 'Completed' && !b.isReviewed && <button className="btn btn-secondary" style={{ padding: '7px 12px', fontSize: '12px' }} onClick={() => setReviewBooking(b)}>Rate</button>}
                        {b.status === 'Pending' && (confirmCancelId === b.id
                          ? <div style={{ display: 'flex', gap: '6px' }}>
                            <button className="btn" style={{ padding: '5px 10px', fontSize: '11px', background: 'var(--surface)', color: 'var(--text-muted)', border: '1px solid var(--border)' }} onClick={() => setConfirmCancelId(null)}>Back</button>
                            <button className="btn" style={{ padding: '5px 10px', fontSize: '11px', background: '#DC2626', color: 'white' }} onClick={async () => { await update(ref(db, `bookings/${b.id}`), { status: 'Cancelled' }); setConfirmCancelId(null); }}>Confirm</button>
                          </div>
                          : <button className="btn" style={{ padding: '6px 12px', fontSize: '12px', background: 'rgba(239, 68, 68, 0.1)', color: 'var(--primary)', border: '1px solid #FECACA' }} onClick={() => setConfirmCancelId(b.id)}>Cancel</button>
                        )}
                        {(b.status === 'Cancelled' || b.status === 'Declined' || b.isReviewed || b.status === 'Refund Approved' || b.status === 'Refund Confirmed' || (b.status || '').toLowerCase() === 'no show') && (confirmDeleteId === b.id
                          ? <div style={{ display: 'flex', gap: '6px' }}>
                            <button className="btn" style={{ padding: '5px 10px', fontSize: '11px', background: 'var(--surface)', color: 'var(--text-muted)', border: '1px solid var(--border)' }} onClick={() => setConfirmDeleteId(null)}>Back</button>
                            <button className="btn" style={{ padding: '5px 10px', fontSize: '11px', background: '#DC2626', color: 'white' }} onClick={async () => { await remove(ref(db, `bookings/${b.id}`)); setConfirmDeleteId(null); }}>Delete</button>
                          </div>
                          : <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                              {(b.status === 'Cancelled' || b.status === 'Declined') && (
                                <>
                                  <button className="btn" style={{ padding: '7px 10px', background: 'var(--surface)', color: '#10B981', border: '1px solid #10B981', fontSize: '11px' }} onClick={(e) => { e.stopPropagation(); handleBookAgain(b); }}>Book Again</button>
                                  <button className="btn" style={{ padding: '7px 10px', background: 'var(--surface)', color: 'var(--primary)', border: '1px solid var(--primary)', fontSize: '11px' }} onClick={(e) => { e.stopPropagation(); handleRequestRefund(b); }}>Request Refund</button>
                                </>
                              )}
                              <button className="btn" style={{ padding: '7px', background: 'var(--light-bg)', color: 'var(--text-muted)', border: '1px solid var(--border)' }} onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(b.id); }}><Trash2 size={16} /></button>
                            </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              {myBookings.length > bookingLimit && (
                <button className="btn" style={{ background: 'var(--light-bg)', color: 'var(--text-main)', border: '1px solid var(--border)', marginTop: '8px' }} onClick={() => setBookingLimit(prev => prev + 5)}>Load More Bookings</button>
              )}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '80px 0', opacity: 0.5 }}>
              <Navigation size={48} style={{ marginBottom: '16px' }} />
              <p style={{ fontWeight: 600 }}>You have no booking history yet.</p>
            </div>
          )}
        </section>
      </div>
    )}


      {activeTab === 'My Expenses' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', maxWidth: '700px', margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
            <Wallet size={20} color="#10B981" />
            <h3 style={{ margin: 0, fontSize: '20px', fontWeight: 800 }}>My Booking Expenses</h3>
          </div>
          {(() => {
            const availableMonths = [...new Set(myBookings.map(b => {
              if (!b.bookingDate || typeof b.bookingDate !== 'string') return null;
              const parts = b.bookingDate.split(' ');
              if (parts.length < 3) return null;
              const yearPart = b.bookingDate.split(', ')[1];
              if (!yearPart) return null;
              return `${parts[0]} ${yearPart}`.trim();
            }).filter(Boolean))];

            const filteredBookings = myBookings.filter(b => {
              if (b.status === 'Cancelled' || b.status === 'Declined' || b.status === 'Refund Approved' || b.status === 'Refund Confirmed') return false;
              if (expenseStatusFilter === 'Completed' && b.status !== 'Completed') return false;
              if (expenseMonthFilter !== 'All') {
                if (!b.bookingDate || typeof b.bookingDate !== 'string') return false;
                const parts = b.bookingDate.split(' ');
                if (parts.length < 3) return false;
                const yearPart = b.bookingDate.split(', ')[1];
                if (!yearPart) return false;
                const monthYear = `${parts[0]} ${yearPart}`.trim();
                if (monthYear !== expenseMonthFilter) return false;
              }
              return true;
            });

            const expensesByProperty = {};
            filteredBookings.forEach(b => {
              const propName = b.propertyName || 'Unknown Property';
              if (!expensesByProperty[propName]) expensesByProperty[propName] = { total: 0, bookings: [] };
              const price = Number(b.totalPrice || 0);
              expensesByProperty[propName].total += price;
              expensesByProperty[propName].bookings.push(b);
            });
            const props = Object.keys(expensesByProperty);
            
            return (
              <>
                <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
                  <select value={expenseMonthFilter} onChange={e => setExpenseMonthFilter(e.target.value)} style={{ padding: '8px 12px', borderRadius: '12px', border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text-main)', fontWeight: 600, outline: 'none', cursor: 'pointer', flex: 1 }}>
                    <option value="All">All Months</option>
                    {availableMonths.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                  <select value={expenseStatusFilter} onChange={e => setExpenseStatusFilter(e.target.value)} style={{ padding: '8px 12px', borderRadius: '12px', border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text-main)', fontWeight: 600, outline: 'none', cursor: 'pointer', flex: 1 }}>
                    <option value="All">All Bookings</option>
                    <option value="Completed">Completed Only</option>
                  </select>
                </div>

                {props.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '60px 0', background: 'var(--light-bg)', borderRadius: '24px', border: '1px dashed var(--border-dashed)' }}>
                    <Wallet size={32} color="var(--text-muted)" style={{ margin: '0 auto 16px' }} />
                    <p style={{ margin: 0, fontWeight: 800, fontSize: '18px' }}>No Expenses</p>
                    <p style={{ margin: '8px 0 0 0', color: 'var(--text-muted)', fontSize: '14px' }}>No bookings match your selected filters.</p>
                  </div>
                ) : (
                  <div style={{ display: 'grid', gap: '24px' }}>
                {props.map(propName => (
                  <div key={propName} className="card" style={{ padding: '24px', borderRadius: '24px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', paddingBottom: '16px', borderBottom: '1px solid var(--border)' }}>
                      <div>
                        <h4 style={{ margin: 0, fontSize: '18px', fontWeight: 800 }}>{propName}</h4>
                        <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: 'var(--text-muted)', fontWeight: 600 }}>{expensesByProperty[propName].bookings.length} Booking(s)</p>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase' }}>Total Spent</p>
                        <p style={{ margin: 0, fontSize: '22px', fontWeight: 900, color: '#10B981' }}>₱{expensesByProperty[propName].total.toLocaleString()}</p>
                      </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {expensesByProperty[propName].bookings.map((b, i) => (
                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--light-bg)', padding: '16px', borderRadius: '16px' }}>
                          <div>
                            <p style={{ margin: 0, fontSize: '14px', fontWeight: 800 }}>{b.activityTitle || b.roomTitle}</p>
                            {(() => {
                              const isAct = b.isActivityBooking === true || (
                                !b.roomId &&
                                !b.roomTitle &&
                                !b.activityTitle?.toLowerCase().includes('room') &&
                                b.nights === undefined &&
                                Boolean(b.activityId && String(b.activityId).trim() !== '')
                              );
                              const duration = parseInt((isAct ? (b.hours || b.nights) : (b.nights || b.hours)) || 1);
                              return (
                                <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: 'var(--text-muted)', fontWeight: 600 }}>
                                  {b.bookingDate} ({duration} {isAct ? 'Hour/s' : 'Nights'})
                                </p>
                              );
                            })()}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <span style={{ fontSize: '15px', fontWeight: 800, color: 'var(--text-main)' }}>₱{Number(b.totalPrice || 0).toLocaleString()}</span>
                            <button className="btn" style={{ padding: '6px 12px', fontSize: '12px', background: 'var(--surface)', color: 'var(--primary)', border: '1px solid rgba(251,54,64,0.2)' }} onClick={() => setShowBreakdownBooking(b)}>Breakdown</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
                )}
              </>
            );
          })()}
        </div>
      )}

      {activeTab === 'My Coupons' && (
        <div className="view-transition">
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
            <Tag size={22} color="var(--primary)" />
            <h3 style={{ margin: 0, fontSize: '20px', fontWeight: 800 }}>Available Coupons & Promos</h3>
          </div>

          {(() => {
            const now = new Date();
            const todayStr = format(now, 'yyyy-MM-dd');

            // 1. Filter active promos (no isEvent filter, newest first)
            const activePromos = promoEvents.filter(promo => {
              if (promo.active === false || String(promo.active).toLowerCase() === 'false') return false;

              if (promo.startDate && String(promo.startDate).trim()) {
                const sClean = String(promo.startDate).trim().split('T')[0];
                if (todayStr < sClean) return false;
              }
              if (promo.endDate && String(promo.endDate).trim()) {
                const eClean = String(promo.endDate).trim().split('T')[0];
                if (todayStr > eClean) return false;
              }
              if (promo.expiresAt) {
                const expTime = typeof promo.expiresAt === 'number' ? promo.expiresAt : new Date(promo.expiresAt).getTime();
                if (!isNaN(expTime) && expTime < now.getTime()) return false;
              }
              return true;
            }).sort((a, b) => {
              const aKey = a.createdAt || a.id || '';
              const bKey = b.createdAt || b.id || '';
              return String(bKey).localeCompare(String(aKey));
            });

            // 2. Filter active personal coupons
            const activeCoupons = userCoupons.filter(coupon => {
              if (coupon.used === true) return false;
              if (coupon.active === false || String(coupon.active).toLowerCase() === 'false') return false;
              if (coupon.startDate && String(coupon.startDate).trim()) {
                const sDate = String(coupon.startDate).trim().split('T')[0];
                if (todayStr < sDate) return false;
              }
              if (coupon.endDate && String(coupon.endDate).trim()) {
                const eDate = String(coupon.endDate).trim().split('T')[0];
                if (todayStr > eDate) return false;
              }
              if (coupon.expiresAt) {
                const expTime = typeof coupon.expiresAt === 'number' ? coupon.expiresAt : new Date(coupon.expiresAt).getTime();
                if (!isNaN(expTime) && expTime < now.getTime()) return false;
              }
              return true;
            });

            if (activePromos.length === 0 && activeCoupons.length === 0) {
              return (
                <div style={{ textAlign: 'center', padding: '60px 0', background: 'var(--light-bg)', borderRadius: '24px', border: '1px dashed var(--border-dashed)' }}>
                  <Tag size={36} color="var(--text-muted)" style={{ margin: '0 auto 16px', opacity: 0.6 }} />
                  <p style={{ margin: 0, fontWeight: 800, fontSize: '18px' }}>No active promos or coupons right now.</p>
                  <p style={{ margin: '8px 0 0 0', color: 'var(--text-muted)', fontSize: '14px' }}>
                    Keep exploring resorts to unlock discounts and check back for new promo events!
                  </p>
                </div>
              );
            }

            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
                {/* Active Promos Section (Events and Promo Codes) */}
                {activePromos.length > 0 && (
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 340px), 1fr))',
                    gap: '24px',
                    width: '100%',
                    alignItems: 'start'
                  }}>
                    {activePromos.map((promo, idx) => {
                      let discountText;
                      if (promo.badge && String(promo.badge).trim()) {
                        discountText = String(promo.badge).trim();
                      } else if (promo.discountType === 'fixed') {
                        discountText = `₱${Number(promo.discountValue || 0).toLocaleString()} OFF`;
                      } else {
                        discountText = `${promo.discountValue || 10}% OFF`;
                      }

                      const appRooms = promo.applicableRooms;
                      let applicable = 'All Rooms';
                      if (Array.isArray(appRooms) && appRooms.length > 0) {
                        if (appRooms.includes('ALL') || appRooms.includes('All Rooms')) {
                          applicable = 'All Rooms';
                        } else {
                          applicable = appRooms.join(', ');
                        }
                      }

                      let dateDisplay = null;
                      if (promo.startDate && promo.endDate) {
                        try {
                          const sDt = new Date(String(promo.startDate).split('T')[0]);
                          const eDt = new Date(String(promo.endDate).split('T')[0]);
                          dateDisplay = `Valid ${format(sDt, 'MMM dd, yyyy')} - ${format(eDt, 'MMM dd, yyyy')}`;
                        } catch (_) {
                          dateDisplay = `Valid ${promo.startDate} - ${promo.endDate}`;
                        }
                      } else if (promo.endDate) {
                        dateDisplay = `Valid until ${promo.endDate}`;
                      } else if (promo.startDate) {
                        dateDisplay = `Valid from ${promo.startDate}`;
                      }

                      const hasImage = Boolean(promo.imageUrl && String(promo.imageUrl).trim());
                      const isEvent = promo.isEvent === true;
                      const promoCode = (promo.code || '').trim();
                      const isCopied = copiedPromoCode === promoCode;

                      return (
                        <div
                          key={promo.id || idx}
                          className="card"
                          style={{
                            padding: 0,
                            borderRadius: '20px',
                            overflow: 'hidden',
                            border: '1.5px solid rgba(251, 54, 64, 0.25)',
                            background: 'var(--surface)',
                            boxShadow: '0 4px 20px rgba(251, 54, 64, 0.06)',
                            display: 'flex',
                            flexDirection: 'column'
                          }}
                        >
                          {hasImage && (
                            <img
                              src={promo.imageUrl}
                              alt={promo.title || 'Promo'}
                              style={{ width: '100%', height: '140px', objectFit: 'cover' }}
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          )}
                          <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', flex: 1 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '14px' }}>
                              <span style={{
                                background: 'var(--primary)',
                                color: 'white',
                                padding: '6px 14px',
                                borderRadius: '20px',
                                fontSize: '13px',
                                fontWeight: 800,
                                letterSpacing: '0.5px'
                              }}>
                                {discountText}
                              </span>
                              <span style={{ fontSize: '12px', fontWeight: 800, color: '#10B981' }}>Active</span>
                            </div>

                            <h4 style={{ margin: '0 0 6px 0', fontSize: '17px', fontWeight: 800 }}>
                              {promo.title || (isEvent ? 'Special Event Promo' : (promoCode || 'Promo Offer'))}
                            </h4>
                            {promo.description && (
                              <p style={{ margin: '0 0 14px 0', fontSize: '13px', color: 'var(--text-muted)', lineHeight: '1.4' }}>
                                {promo.description}
                              </p>
                            )}

                            {isEvent ? (
                              <div style={{
                                background: 'rgba(16, 185, 129, 0.1)',
                                border: '1px solid rgba(16, 185, 129, 0.3)',
                                borderRadius: '10px',
                                padding: '10px 14px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                marginBottom: '14px',
                                color: '#10B981',
                                fontSize: '12px',
                                fontWeight: 800
                              }}>
                                <CheckCircle size={16} />
                                <span>Auto-applied at booking. No code needed.</span>
                              </div>
                            ) : promoCode ? (
                              <div style={{ marginBottom: '14px' }}>
                                <div style={{
                                  background: 'rgba(251, 54, 64, 0.05)',
                                  border: '1px solid rgba(251, 54, 64, 0.2)',
                                  padding: '10px 14px',
                                  borderRadius: '12px',
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  marginBottom: '6px'
                                }}>
                                  <span style={{ fontSize: '12px', fontWeight: 700, color: 'var(--text-muted)' }}>CODE</span>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span style={{ fontFamily: 'monospace', fontSize: '15px', fontWeight: 900, color: 'var(--primary)', letterSpacing: '1px' }}>
                                      {promoCode}
                                    </span>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        navigator.clipboard.writeText(promoCode);
                                        setCopiedPromoCode(promoCode);
                                        setTimeout(() => setCopiedPromoCode(null), 2000);
                                      }}
                                      style={{
                                        background: 'transparent',
                                        border: 'none',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        padding: '4px',
                                        color: isCopied ? '#10B981' : 'var(--primary)'
                                      }}
                                      title="Copy code"
                                    >
                                      {isCopied ? <CheckCircle size={15} /> : <Copy size={15} />}
                                    </button>
                                  </div>
                                </div>
                                <span style={{ fontSize: '12px', fontStyle: 'italic', color: 'var(--text-muted)' }}>
                                  Enter this code in the 'Have a Promo Code?' box when booking.
                                </span>
                              </div>
                            ) : null}

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '12px', color: 'var(--text-muted)', marginTop: 'auto' }}>
                              <div>
                                <strong>Applicable Rooms:</strong> {applicable}
                              </div>
                              {dateDisplay && (
                                <div>
                                  <strong>Valid Dates:</strong> {dateDisplay}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Personal Coupons */}
                {activeCoupons.length > 0 && (
                  <div>
                    {activePromos.length > 0 && (
                      <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 800 }}>
                        Your Coupon Codes
                      </h4>
                    )}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 340px), 1fr))',
                      gap: '24px',
                      width: '100%',
                      alignItems: 'start'
                    }}>
                      {activeCoupons.map((coupon, idx) => {
                        const discountText = coupon.discountType === 'percentage'
                          ? `${coupon.discountValue || 10}% OFF`
                          : `₱${Number(coupon.discountValue || 0).toLocaleString()} OFF`;
                        const applicable = Array.isArray(coupon.applicableRooms) && coupon.applicableRooms.length > 0
                          ? coupon.applicableRooms.join(', ')
                          : 'All Rooms';

                        const isCopied = copiedPromoCode === coupon.code;

                        return (
                          <div
                            key={coupon.code || idx}
                            className="card"
                            style={{
                              padding: '24px',
                              borderRadius: '20px',
                              position: 'relative',
                              border: '1.5px solid rgba(251, 54, 64, 0.25)',
                              background: 'var(--surface)',
                              boxShadow: '0 4px 20px rgba(251, 54, 64, 0.06)'
                            }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '14px' }}>
                              <span style={{
                                background: 'var(--primary)',
                                color: 'white',
                                padding: '6px 14px',
                                borderRadius: '20px',
                                fontSize: '13px',
                                fontWeight: 800,
                                letterSpacing: '0.5px'
                              }}>
                                {discountText}
                              </span>
                              <span style={{ fontSize: '12px', fontWeight: 800, color: '#10B981' }}>Active</span>
                            </div>

                            <h4 style={{ margin: '0 0 6px 0', fontSize: '17px', fontWeight: 800 }}>
                              {coupon.title || coupon.code}
                            </h4>
                            <p style={{ margin: '0 0 16px 0', fontSize: '13px', color: 'var(--text-muted)', lineHeight: '1.4' }}>
                              {coupon.description || 'Applicable during booking checkout.'}
                            </p>

                            <div style={{
                              background: 'rgba(251, 54, 64, 0.05)',
                              padding: '10px 14px',
                              borderRadius: '12px',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              marginBottom: '12px'
                            }}>
                              <span style={{ fontSize: '12px', fontWeight: 700, color: 'var(--text-muted)' }}>CODE</span>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ fontFamily: 'monospace', fontSize: '15px', fontWeight: 900, color: 'var(--primary)', letterSpacing: '1px' }}>
                                  {coupon.code}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => {
                                    navigator.clipboard.writeText(coupon.code);
                                    setCopiedPromoCode(coupon.code);
                                    setTimeout(() => setCopiedPromoCode(null), 2000);
                                  }}
                                  style={{
                                    background: 'transparent',
                                    border: 'none',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    padding: '4px',
                                    color: isCopied ? '#10B981' : 'var(--primary)'
                                  }}
                                  title="Copy coupon code"
                                >
                                  {isCopied ? <CheckCircle size={15} /> : <Copy size={15} />}
                                </button>
                              </div>
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '12px', color: 'var(--text-muted)' }}>
                              <div>
                                <strong>Applicable Rooms:</strong> {applicable}
                              </div>
                              {coupon.startDate && coupon.endDate && (
                                <div>
                                  <strong>Valid:</strong> {coupon.startDate} to {coupon.endDate}
                                </div>
                              )}
                              {coupon.expiresAt && !coupon.endDate && (
                                <div>
                                  <strong>Expires:</strong> {new Date(coupon.expiresAt).toLocaleDateString()}
                                </div>
                              )}
                              {coupon.roomOnly && (
                                <div style={{ color: '#F59E0B', fontWeight: 600 }}>
                                  *Valid for room booking reservation only
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      )}

      {activeTab === 'Chat' && (
        <section className="view-transition">
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '24px' }}>
            <MessageSquare size={20} color="var(--secondary)" />
            <h3 style={{ margin: 0, fontSize: '22px', fontWeight: 800 }}>Messages & Inquiries</h3>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '16px' }}>
            {chatRooms.length > 0 ? chatRooms.map(room => (
              <ChatRoomItem
                key={room.otherUid}
                room={room}
                onClick={(r) => setSelectedChat({ id: r.otherUid, name: r.otherUserName })}
              />
            )) : (
              <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: '80px 0', opacity: 0.5 }}>
                <MessageSquare size={48} style={{ marginBottom: '16px' }} />
                <p style={{ fontWeight: 600 }}>No active conversations.</p>
              </div>
            )}
          </div>
        </section>
      )}

      {selectedBooking && (
        <div className="modal-overlay" onClick={() => setSelectedBooking(null)}>
          <div className="card modal-content" style={{ maxWidth: '380px', textAlign: 'center' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8px' }}>
              <button onClick={() => setSelectedBooking(null)} className="close-btn"><X size={18} /></button>
            </div>
            <h3 style={{ fontWeight: 800 }}>Booking QR Code</h3>
            <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '20px' }}>Show this to the resort staff at check-in</p>
            <div style={{ background: '#FFFFFF', padding: '24px', borderRadius: '24px', display: 'inline-block', boxShadow: '0 8px 30px rgba(0,0,0,0.06)', border: '2px solid var(--border)' }}>
              <QRCodeCanvas value={`${window.location.origin}/owner?scan=${selectedBooking.id}`} size={220} bgColor="#FFFFFF" fgColor="#000000" />
            </div>
            <p style={{ fontSize: '11px', color: '#999', marginTop: '20px', letterSpacing: '1px' }}>ID: {selectedBooking.id}</p>
          </div>
        </div>
      )}

      {(() => {
        let isMissedDetail = false;
        if (detailBooking) {
          try {
            if (detailBooking.bookingDate && detailBooking.bookingDate !== 'N/A') {
              const parsedDate = new Date(detailBooking.bookingDate);
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              if (parsedDate < today) isMissedDetail = true;
            }
          } catch(e) {}
            const isAct = detailBooking.isActivityBooking === true || (
              !detailBooking.roomId &&
              !detailBooking.roomTitle &&
              !detailBooking.activityTitle?.toLowerCase().includes('room') &&
              detailBooking.nights === undefined &&
              Boolean(detailBooking.activityId && String(detailBooking.activityId).trim() !== '')
            );
            const duration = parseInt((isAct ? (detailBooking.hours || detailBooking.nights) : (detailBooking.nights || detailBooking.hours)) || 1);

            return (
              <div className="modal-overlay" onClick={() => setDetailBooking(null)}>
                <div className="card modal-content" style={{ maxWidth: '480px', padding: '32px', borderRadius: '28px' }} onClick={e => e.stopPropagation()}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3 style={{ margin: 0, fontWeight: 900, fontSize: '20px' }}>{detailBooking.propertyName}</h3>
                <button onClick={() => setDetailBooking(null)} className="close-btn"><X size={18} /></button>
              </div>
              <span className={`status-badge status-${(detailBooking.status || 'pending').toLowerCase().replace(/ /g, '-')}`} style={{ marginBottom: '20px', display: 'inline-flex' }}>{detailBooking.status}</span>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginTop: '12px' }}>
                {[[isAct ? 'Activity' : 'Room', detailBooking.activityTitle || detailBooking.roomTitle || 'N/A'],
                [isAct ? 'Date' : 'Check-in Date', detailBooking.bookingDate || 'N/A'],
                [isAct ? 'Hours' : 'Nights', `${duration} ${isAct ? (duration === 1 ? 'Hour' : 'Hours') : (duration === 1 ? 'Night' : 'Nights')}`],
                ['Arrival Time', detailBooking.arrivalTime || detailBooking.timeSlot || detailBooking.bookingTime || (isAct ? 'Regular Operating Hours' : 'Check-in Time')],
                ['Guest Name', detailBooking.touristName || 'N/A'],
                ['Payment Method', detailBooking.paymentMethod || 'N/A'],
                ['Total Amount', `₱${Number(detailBooking.totalPrice || 0).toLocaleString()}`],
                ['Amount Paid', `₱${Number(detailBooking.amountPaid || 0).toLocaleString()}`],
                ['Balance', `₱${(['Cancelled', 'Declined', 'Refunded', 'Refund Approved', 'Refund Confirmed', 'Completed'].includes(detailBooking.status) ? 0 : Math.max(0, Number(detailBooking.totalPrice || 0) - Number(detailBooking.amountPaid || 0))).toLocaleString()}`],
                ].map(([label, val]) => (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
                  <span style={{ fontSize: '13px', color: 'var(--text-muted)', fontWeight: 600 }}>{label}</span>
                  <span style={{ fontSize: '14px', fontWeight: 800 }}>{val}</span>
                </div>
              ))}
            </div>
            {detailBooking.status === 'Refund Approved' && (
              <div style={{ marginTop: '14px', padding: '12px 14px', background: 'rgba(16, 185, 129, 0.1)', border: '1px solid rgba(16, 185, 129, 0.25)', borderRadius: '12px', fontSize: '13px', color: '#047857', fontWeight: 700 }}>
                Refund Approved! Please wait for a moment while the owner processes and transfers the payout to your GCash account.
              </div>
            )}
            {detailBooking.status === 'Refund Confirmed' && (
              <div style={{ marginTop: '14px', padding: '12px 14px', background: 'rgba(16, 185, 129, 0.1)', border: '1px solid rgba(16, 185, 129, 0.25)', borderRadius: '12px', fontSize: '13px', color: '#047857', fontWeight: 700 }}>
                Your refund has been confirmed by the resort.
              </div>
            )}
            {detailBooking.selectedAddons?.length > 0 && (
              <div style={{ marginTop: '14px', padding: '12px', background: 'var(--light-bg)', borderRadius: '12px' }}>
                <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--text-muted)', marginBottom: '6px' }}>ADD-ONS</div>
                <div style={{ fontSize: '14px', fontWeight: 600 }}>{detailBooking.selectedAddons.join(', ')}</div>
              </div>
            )}
            <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'center' }}>
              <button className="btn" disabled={isMissedDetail} style={{ background: isMissedDetail ? '#f3f4f6' : 'rgba(79, 70, 229, 0.1)', color: isMissedDetail ? '#9ca3af' : '#4F46E5', fontSize: '13px', fontWeight: 800, border: isMissedDetail ? '1px solid #e5e7eb' : '1px solid rgba(79, 70, 229, 0.2)', width: '100%', display: 'flex', gap: '8px', alignItems: 'center', justifyContent: 'center' }} onClick={isMissedDetail ? null : () => setShowBreakdownBooking(detailBooking)}>
                <ShoppingBag size={16} /> View Price Breakdown
              </button>
            </div>
            <div style={{ marginTop: '20px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              {(detailBooking.status === 'Confirmed' || detailBooking.status === 'Checked In' || detailBooking.status === 'Refund Declined') && (
                <>
                  <button className="btn btn-primary" disabled={isMissedDetail} style={{ flex: 1, minWidth: '120px', background: isMissedDetail ? '#f3f4f6' : undefined, color: isMissedDetail ? '#9ca3af' : undefined, border: isMissedDetail ? '1px solid #e5e7eb' : undefined }} onClick={isMissedDetail ? null : () => { setDetailBooking(null); setSelectedBooking(detailBooking); }}><QrCode size={16} /> Show QR</button>
                  <button className="btn" disabled={isMissedDetail} style={{ flex: 1, minWidth: '120px', background: isMissedDetail ? '#f3f4f6' : '#F5F3FF', color: isMissedDetail ? '#9ca3af' : '#7C3AED', border: isMissedDetail ? '1px solid #e5e7eb' : '1px solid rgba(124,58,237,0.2)' }} onClick={() => { setDetailBooking(null); setBillSplitterBooking(detailBooking); }}><Split size={14} /> Split Bill</button>
                </>
              )}
              {(detailBooking.status === 'Confirmed' || detailBooking.status === 'Pending') && (
                <div style={{ width: '100%', display: 'flex', gap: '10px' }}>
                  <button className="btn" disabled={isMissedDetail} style={{ flex: 1, background: isMissedDetail ? '#f3f4f6' : 'var(--surface)', color: isMissedDetail ? '#9ca3af' : 'var(--secondary)', border: isMissedDetail ? '1px solid #e5e7eb' : '1px solid var(--secondary)' }} onClick={() => { setDetailBooking(null); setRescheduleBooking(detailBooking); }}><CalendarDays size={14} /> Reschedule</button>
                  <button className="btn" disabled={isMissedDetail} style={{ flex: 1, background: isMissedDetail ? '#f3f4f6' : 'var(--surface)', color: isMissedDetail ? '#9ca3af' : '#DC2626', border: isMissedDetail ? '1px solid #e5e7eb' : '1px solid #DC2626' }} onClick={() => { setDetailBooking(null); setRefundBooking(detailBooking); }}><CreditCard size={14} /> Refund</button>
                </div>
              )}
              {detailBooking.status === 'Reschedule Requested' && (
                <div style={{ width: '100%', display: 'flex', gap: '10px' }}>
                  <button className="btn" style={{ flex: 1, background: 'rgba(239, 68, 68, 0.1)', color: '#DC2626', border: '1px solid #FECACA' }} onClick={async () => {
                    await update(ref(db, `bookings/${detailBooking.id}`), {
                      status: 'Confirmed',
                      requestedRescheduleDate: null,
                      requestedRescheduleNights: null
                    });
                    setDetailBooking(null);
                  }}>
                    <X size={14} /> Cancel Reschedule
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }
    return null;
  })()}

      {reviewBooking && <ReviewModal booking={reviewBooking} onClose={() => setReviewBooking(null)} />}
      {rescheduleBooking && (() => {
        const isAct = rescheduleBooking.isActivityBooking === true || (
          !rescheduleBooking.roomId &&
          !rescheduleBooking.roomTitle &&
          !rescheduleBooking.activityTitle?.toLowerCase().includes('room') &&
          rescheduleBooking.nights === undefined &&
          Boolean(rescheduleBooking.activityId && String(rescheduleBooking.activityId).trim() !== '')
        );
        return isAct ? (
          <RescheduleModal booking={rescheduleBooking} onClose={() => setRescheduleBooking(null)} />
        ) : (
          <RescheduleRoomModal booking={rescheduleBooking} onClose={() => setRescheduleBooking(null)} />
        );
      })()}
      {refundBooking && <RefundModal booking={refundBooking} onClose={() => setRefundBooking(null)} />}
      {billSplitterBooking && (
        <BillSplitterModal
          onClose={() => setBillSplitterBooking(null)}
          initialAmount={billSplitterBooking.totalPrice}
          resortGCash={(() => {
            const prop = properties.find(p => p.id === billSplitterBooking.propertyId);
            return prop && prop.gcashNumber ? `GCash ${prop.gcashNumber} - ${prop.gcashName || 'Resort'}` : null;
          })()}
          addons={(() => {
            const prop = properties.find(p => p.id === billSplitterBooking.propertyId);
            let extracted = [];
            if (prop && prop.addonPrices) {
              Object.keys(prop.addonPrices).forEach(name => {
                extracted.push({ name: name, price: prop.addonPrices[name] });
              });
            }
            return extracted;
          })()}
          bookedItems={(() => {
            let items = [];
            const isOldBooking = !billSplitterBooking.pricing && billSplitterBooking.selectedAddons?.length > 0;
            let calculatedAddonsTotal = billSplitterBooking.pricing?.addonsTotal || 0;
            const targetProp = properties.find(p => p.id === billSplitterBooking.propertyId);

            if (isOldBooking) {
              billSplitterBooking.selectedAddons.forEach(addonStr => {
                try {
                  const match = addonStr.match(/(.+?)\s*\(x(\d+)\)/);
                  if (match && targetProp?.addonPrices) {
                    const name = match[1].trim();
                    const qty = parseInt(match[2]);
                    const pricePerUnit = targetProp.addonPrices[name] || 0;
                    calculatedAddonsTotal += pricePerUnit * qty;
                    items.push({ name: `${name} (x${qty})`, amount: (pricePerUnit * qty).toString(), assignedTo: 'All' });
                  } else {
                     items.push({ name: addonStr, amount: '0', assignedTo: 'All' });
                  }
                } catch (e) { }
              });
            } else if (billSplitterBooking.pricing?.addonsList?.length > 0) {
              billSplitterBooking.pricing.addonsList.forEach(a => {
                items.push({ name: `${a.name} (x${a.quantity})`, amount: a.total.toString(), assignedTo: 'All' });
              });
            }

            const grandTotal = billSplitterBooking.pricing?.grandTotal || billSplitterBooking.totalPrice || 0;
            const basePrice = billSplitterBooking.pricing?.basePrice || Math.max(0, grandTotal - calculatedAddonsTotal);
            const isActBS = billSplitterBooking.isActivityBooking === true || (
              !billSplitterBooking.roomId &&
              !billSplitterBooking.roomTitle &&
              !billSplitterBooking.activityTitle?.toLowerCase().includes('room') &&
              billSplitterBooking.nights === undefined &&
              Boolean(billSplitterBooking.activityId && String(billSplitterBooking.activityId).trim() !== '')
            );
            const durationBS = parseInt((isActBS ? (billSplitterBooking.hours || billSplitterBooking.nights) : (billSplitterBooking.nights || billSplitterBooking.hours)) || 1);
            items.unshift({ name: isActBS ? `Activity Base (${durationBS} Hour/s)` : `Room Base (${billSplitterBooking.nights || 1} Night/s)`, amount: basePrice.toString(), assignedTo: 'All' });
            return items;
          })()}
          guestCount={billSplitterBooking.guests || 2}
        />
      )}

      {showGlobalSplitter && <BillSplitterModal onClose={() => setShowGlobalSplitter(false)} />}

      {showBreakdownBooking && (
        <div className="modal-overlay" onClick={() => setShowBreakdownBooking(null)} style={{ zIndex: 6000 }}>
          <div className="card modal-content" style={{ maxWidth: '450px', background: 'var(--surface)', borderRadius: '24px' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h3 style={{ margin: 0, fontWeight: 800, fontSize: '20px' }}>Price Breakdown</h3>
              <button onClick={() => setShowBreakdownBooking(null)} className="close-btn"><X size={18} /></button>
            </div>

            {(() => {
              const isOldBooking = !showBreakdownBooking.pricing && showBreakdownBooking.selectedAddons?.length > 0;
              let calculatedAddonsTotal = showBreakdownBooking.pricing?.addonsTotal || 0;

              if (isOldBooking) {
                const targetProp = properties.find(p => p.uid === showBreakdownBooking.ownerUid || p.id === showBreakdownBooking.propertyId);
                showBreakdownBooking.selectedAddons.forEach(addonStr => {
                  try {
                    const match = addonStr.match(/(.+?)\s*\(x(\d+)\)/);
                    if (match && targetProp?.addonPrices) {
                      const name = match[1].trim();
                      const qty = parseInt(match[2]);
                      const pricePerUnit = targetProp.addonPrices[name] || 0;
                      calculatedAddonsTotal += pricePerUnit * qty;
                    }
                  } catch (e) { }
                });
              }

              const grandTotal = Number(showBreakdownBooking.totalPrice ?? showBreakdownBooking.pricing?.grandTotal ?? 0);

              // Read promo discount in specified fallback order
              const rawDiscount = showBreakdownBooking.promoDiscount ??
                showBreakdownBooking.pricing?.promoDiscount ??
                showBreakdownBooking.pricing?.discount ??
                showBreakdownBooking.discountAmount;
              let discountAmount = Math.abs(Number(rawDiscount || 0));
              if (isNaN(discountAmount)) discountAmount = 0;

              const rawCode = (showBreakdownBooking.promoCode || '').trim();
              const rawName = (showBreakdownBooking.promoName || '').trim();
              let discountLabel = "Promo Discount";
              if (rawCode) {
                discountLabel = `Promo Discount (${rawCode})`;
              } else if (rawName) {
                discountLabel = `Promo Discount (${rawName})`;
              }

              const taxes = Number(showBreakdownBooking.pricing?.taxes ?? showBreakdownBooking.pricing?.taxesAndFees ?? 0);

              let basePrice = showBreakdownBooking.pricing?.basePrice;
              if (basePrice === undefined || basePrice === null) {
                basePrice = Math.max(0, grandTotal - calculatedAddonsTotal + discountAmount - taxes);
              } else {
                basePrice = Number(basePrice);
              }

              const isActBD = showBreakdownBooking.isActivityBooking === true || (
                !showBreakdownBooking.roomId &&
                !showBreakdownBooking.roomTitle &&
                !showBreakdownBooking.activityTitle?.toLowerCase().includes('room') &&
                showBreakdownBooking.nights === undefined &&
                Boolean(showBreakdownBooking.activityId && String(showBreakdownBooking.activityId).trim() !== '')
              );
              const durationBD = parseInt((isActBD ? (showBreakdownBooking.hours || showBreakdownBooking.nights) : (showBreakdownBooking.nights || showBreakdownBooking.hours)) || 1);
              const baseLabel = isActBD ? `Activity Base (${durationBD} Hour/s)` : `Room Base (${showBreakdownBooking.nights || 1} Night/s)`;

              return (
                <div style={{ background: 'var(--light-bg)', padding: '20px', borderRadius: '16px', display: 'flex', flexDirection: 'column', gap: '12px', border: '1px solid var(--border)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700 }}>
                    <span style={{ color: 'var(--text-main)' }}>{baseLabel}</span>
                    <span style={{ color: 'var(--text-main)' }}>₱{basePrice.toLocaleString()}</span>
                  </div>

                  {(calculatedAddonsTotal > 0 || showBreakdownBooking.selectedAddons?.length > 0) && (
                    <>
                      <div style={{ height: '1px', background: 'var(--border)', margin: '8px 0' }} />
                      <div style={{ fontWeight: 800, fontSize: '12px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>ADD-ONS</div>

                      {/* New bookings use addonsList, old bookings use selectedAddons array */}
                      {showBreakdownBooking.pricing?.addonsList?.length > 0 ? (
                        showBreakdownBooking.pricing.addonsList.map((addon, idx) => (
                          <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', color: 'var(--text-muted)' }}>
                            <span>{addon.name}: ₱{(addon.total / addon.quantity).toLocaleString()} (x{addon.quantity})</span>
                            <span style={{ fontWeight: 600 }}>₱{addon.total.toLocaleString()}</span>
                          </div>
                        ))
                      ) : (
                        showBreakdownBooking.selectedAddons?.map((addonStr, idx) => {
                          let displayPrice = "Included in subtotal";
                          let displayName = addonStr;
                          try {
                            const targetProp = properties.find(p => p.uid === showBreakdownBooking.ownerUid || p.id === showBreakdownBooking.propertyId);
                            const match = addonStr.match(/(.+?)\s*\(x(\d+)\)/);
                            if (match && targetProp?.addonPrices) {
                              const name = match[1].trim();
                              const qty = parseInt(match[2]);
                              const pricePerUnit = targetProp.addonPrices[name] || 0;
                              if (pricePerUnit > 0) {
                                displayName = `${name}: ₱${pricePerUnit.toLocaleString()} (x${qty})`;
                                displayPrice = `₱${(pricePerUnit * qty).toLocaleString()}`;
                              }
                            }
                          } catch (e) { }
                          return (
                            <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', color: 'var(--text-muted)' }}>
                              <span>{displayName}</span>
                              <span style={{ fontWeight: 600 }}>{displayPrice}</span>
                            </div>
                          );
                        })
                      )}

                      <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, marginTop: '8px' }}>
                        <span style={{ color: 'var(--text-main)' }}>Add-ons Subtotal</span>
                        <span style={{ color: 'var(--text-main)' }}>₱{calculatedAddonsTotal.toLocaleString()}</span>
                      </div>
                    </>
                  )}

                  {discountAmount > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, color: '#10B981', marginTop: '4px' }}>
                      <span>{discountLabel}</span>
                      <span>-₱{discountAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                  )}

                  {taxes > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, color: 'var(--text-muted)', marginTop: '4px' }}>
                      <span>Taxes & Fees</span>
                      <span>₱{taxes.toLocaleString()}</span>
                    </div>
                  )}

                  <div style={{ height: '2px', background: 'var(--border)', margin: '12px 0' }} />
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontWeight: 800, fontSize: '16px' }}>Grand Total</span>
                    <span style={{ fontWeight: 900, fontSize: '24px', color: 'var(--primary)' }}>₱{grandTotal.toLocaleString()}</span>
                  </div>

                  {(showBreakdownBooking.amountPaid && showBreakdownBooking.amountPaid < grandTotal) && (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px' }}>
                        <span style={{ fontWeight: 600, fontSize: '14px', color: 'var(--text-muted)' }}>Amount Paid (Downpayment)</span>
                        <span style={{ fontWeight: 700, fontSize: '14px', color: 'var(--text-main)' }}>₱{showBreakdownBooking.amountPaid.toLocaleString()}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px' }}>
                        <span style={{ fontWeight: 800, fontSize: '15px', color: 'var(--text-main)' }}>Outstanding Balance</span>
                        <span style={{ fontWeight: 900, fontSize: '18px', color: '#ff9800' }}>₱{(grandTotal - showBreakdownBooking.amountPaid).toLocaleString()}</span>
                      </div>
                      <div style={{ fontSize: '12px', color: 'var(--text-muted)', fontStyle: 'italic', marginTop: '4px', textAlign: 'right' }}>
                        *To be paid upon check-in
                      </div>
                    </>
                  )}
                </div>
              );
            })()}
            <button className="btn btn-primary" style={{ width: '100%', marginTop: '24px', padding: '14px', fontSize: '15px' }} onClick={() => setShowBreakdownBooking(null)}>Close</button>
          </div>
        </div>
      )}

      {showScanner && (
        <QrScanner
          rawMode={true}
          title="Scan Split Bill"
          subtitle="Use your camera to scan a friend's Bill Breakdown QR Code."
          onResult={(text) => {
            setShowScanner(false);
            if (text.includes('Bill Split Summary') || text.includes('Bill Breakdown') || text.includes('Personal Bill')) {
              setScannedBillData(text);
            } else {
              alert("Security Check: Invalid QR Code. This scanner is only for Split Bills.");
            }
          }}
          onClose={() => setShowScanner(false)}
        />
      )}

      {scannedBillData && (
        <div className="modal-overlay" style={{ zIndex: 6000 }}>
          <div className="card modal-content" style={{ maxWidth: '400px', background: 'var(--surface)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h3 style={{ margin: 0, fontWeight: 800 }}>Split Bill Breakdown</h3>
              <button onClick={() => setScannedBillData(null)} className="close-btn"><X size={20} /></button>
            </div>
            <div style={{ background: 'var(--light-bg)', padding: '24px', borderRadius: '16px', whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '15px', lineHeight: '1.6' }}>
              {scannedBillData.replace(/\\n/g, '\n')}
            </div>
          </div>
        </div>
      )}

      <button
        onClick={() => setShowAiBot(!showAiBot)}
        style={{
          position: 'fixed', bottom: '30px', right: '30px',
          width: '60px', height: '60px', borderRadius: '50%',
          background: 'var(--primary)', color: 'white', border: 'none',
          boxShadow: '0 8px 25px rgba(251, 54, 64, 0.4)',
          display: 'flex', justifyContent: 'center', alignItems: 'center',
          cursor: 'pointer', zIndex: 4500, transition: 'var(--transition)'
        }}
        onMouseOver={e => e.currentTarget.style.transform = 'scale(1.1)'}
        onMouseOut={e => e.currentTarget.style.transform = 'scale(1)'}
      >
        {showAiBot ? <X size={24} /> : <Bot size={28} />}
      </button>

      {showAiBot && <AiChatBot onClose={() => setShowAiBot(false)} />}

      {showGcashPrompt && (
        <div className="modal-overlay" onClick={() => setShowGcashPrompt(false)} style={{ zIndex: 6000 }}>
          <div className="card modal-content" style={{ maxWidth: '420px', textAlign: 'center', padding: '28px' }} onClick={e => e.stopPropagation()}>
            <div style={{ width: '52px', height: '52px', borderRadius: '50%', background: 'rgba(245, 158, 11, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px auto', color: '#D97706' }}>
              <CreditCard size={28} />
            </div>
            <h3 style={{ margin: '0 0 12px 0', fontWeight: 800 }}>
              {gcashPromptReason === 'first_time' ? 'Complete Your Profile & Payment Details' : 'GCash Details Required for Refund'}
            </h3>
            <p style={{ color: 'var(--text-muted)', fontSize: '14px', lineHeight: '1.5', marginBottom: '16px' }}>
              {gcashPromptReason === 'first_time'
                ? 'Welcome! Please complete your profile with your Contact Phone Number (09XXXXXXXXX), GCash Number, and GCash Registered Name to ensure smooth bookings, communication, and refund processing.'
                : 'You cannot request a refund for room or activity bookings without setting up your GCash details. Approved refunds are transferred directly to your GCash account.'}
            </p>
            <div style={{ background: 'rgba(245, 158, 11, 0.08)', border: '1px solid rgba(245, 158, 11, 0.25)', borderRadius: '10px', padding: '10px 14px', marginBottom: '22px', fontSize: '12px', color: '#B45309', textAlign: 'left', lineHeight: '1.4' }}>
              ⚠️ <strong>Reminder:</strong> Phone numbers must start with <strong>09</strong> and contain exactly <strong>11 digits</strong>. Double-check your details to avoid delays.
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button className="btn" style={{ flex: 1, background: 'var(--surface)', color: 'var(--text-muted)', border: '1px solid var(--border)' }} onClick={() => setShowGcashPrompt(false)}>Later</button>
              <button className="btn btn-primary" style={{ flex: 1.3 }} onClick={() => { setShowGcashPrompt(false); if(onEditProfile) onEditProfile(); }}>Go to Profile</button>
            </div>
          </div>
        </div>
      )}

      {/* System Navigation Tutorial Tour */}
      <SystemTutorialModal
        isOpen={showTutorial}
        onClose={() => {
          if (uid) {
            localStorage.setItem(`tutorial_seen_${uid}`, 'true');
            sessionStorage.setItem(`tutorial_seen_${uid}`, 'true');
            update(ref(db, `users/${uid}`), { hasSeenTutorial: true }).catch(() => {});
          }
          setShowTutorial(false);
        }}
        onComplete={() => {
          if (uid) {
            localStorage.setItem(`tutorial_seen_${uid}`, 'true');
            sessionStorage.setItem(`tutorial_seen_${uid}`, 'true');
            update(ref(db, `users/${uid}`), { hasSeenTutorial: true }).catch(() => {});
          }
          setShowTutorial(false);
        }}
      />

      {activeTab === 'Partners' && (
        <footer style={{ marginTop: '40px', paddingTop: '20px', paddingBottom: '20px', textAlign: 'center', borderTop: '1px solid var(--border)' }}>
          <button onClick={() => setShowTerms(true)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: '13px', textDecoration: 'underline', cursor: 'pointer' }}>
            Platform Terms & Policies
          </button>
        </footer>
      )}
      {showTerms && <TermsAndPolicies onClose={() => setShowTerms(false)} />}

      {/* Review Lightbox Photo Viewer */}
      {viewerPhotos && (
        <ReviewPhotoViewer
          photos={viewerPhotos}
          initialIndex={viewerIndex}
          onClose={() => setViewerPhotos(null)}
        />
      )}
    </div>
  );
};

const PropertyCard = ({ prop, isFav, onFav, onClick }) => {
  const [rating, setRating] = useState(0);
  const [count, setCount] = useState(0);
  const [imgIndex, setImgIndex] = useState(0);

  const imageUrls = Array.isArray(prop.imageUrls)
    ? prop.imageUrls.filter(u => u)
    : (typeof prop.imageUrls === 'object' && prop.imageUrls !== null
      ? Object.values(prop.imageUrls)
      : []);

  useEffect(() => {
    const reviewRef = ref(db, `reviews/${prop.uid || prop.id}`);
    const unsubscribe = onValue(reviewRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const vals = Object.values(data);
        const avg = vals.reduce((a, b) => a + (b.rating || 0), 0) / vals.length;
        setRating(avg);
        setCount(vals.length);
      }
    });
    return () => unsubscribe();
  }, [prop.uid, prop.id]);

  const nextImg = (e) => {
    e.stopPropagation();
    setImgIndex((prev) => (prev + 1) % imageUrls.length);
  };

  const prevImg = (e) => {
    e.stopPropagation();
    setImgIndex((prev) => (prev - 1 + imageUrls.length) % imageUrls.length);
  };

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden', cursor: 'pointer' }} onClick={onClick}>
      <div style={{ position: 'relative', height: '220px' }}>
        <img
          src={imageUrls[imgIndex] || 'https://via.placeholder.com/400x300?text=No+Image'}
          alt=""
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />

        {imageUrls.length > 1 && (
          <>
            <button onClick={prevImg} style={{
              position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)',
              background: 'rgba(255,255,255,0.8)', border: 'none', borderRadius: '50%',
              width: '30px', height: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}>
              <ChevronLeft size={18} />
            </button>
            <button onClick={nextImg} style={{
              position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)',
              background: 'rgba(255,255,255,0.8)', border: 'none', borderRadius: '50%',
              width: '30px', height: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}>
              <ChevronRight size={18} />
            </button>
            <div style={{
              position: 'absolute', bottom: '12px', left: '50%', transform: 'translateX(-50%)',
              display: 'flex', gap: '6px'
            }}>
              {imageUrls.map((_, i) => (
                <div key={i} style={{
                  width: '6px', height: '6px', borderRadius: '50%',
                  background: i === imgIndex ? 'white' : 'rgba(255,255,255,0.5)',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.2)'
                }}></div>
              ))}
            </div>
          </>
        )}

        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.2), transparent 40%, rgba(0,0,0,0.4))',
          pointerEvents: 'none'
        }}></div>

        <button onClick={onFav} style={{
          position: 'absolute', top: '16px', right: '16px',
          background: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(4px)',
          borderRadius: '14px', border: 'none', padding: '10px',
          cursor: 'pointer', display: 'flex', boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
        }}>
          <Heart size={20} fill={isFav ? 'var(--primary)' : 'none'} color={isFav ? 'var(--primary)' : '#4B5563'} />
        </button>

        <div style={{
          position: 'absolute', top: '16px', left: '16px',
          background: 'var(--primary)', color: 'white',
          padding: '6px 14px', borderRadius: '10px', fontSize: '11px', fontWeight: 800,
          textTransform: 'uppercase', letterSpacing: '0.5px'
        }}>
          {prop.type || 'Resort'}
        </div>

        <div style={{ position: 'absolute', bottom: '16px', left: '16px', display: 'flex', alignItems: 'center', gap: '6px' }}>
          <div style={{
            background: 'rgba(255,255,255,0.95)', padding: '4px 10px',
            borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '4px',
            fontSize: '13px', fontWeight: 800, color: '#000', boxShadow: '0 4px 10px rgba(0,0,0,0.1)'
          }}>
            <Star size={14} fill="#FFD700" color="#FFD700" />
            {rating > 0 ? rating.toFixed(1) : "0.0"}
          </div>
          <span style={{ color: 'white', textShadow: '0 2px 4px rgba(0,0,0,0.5)', fontSize: '11px', fontWeight: 700 }}>({count} Reviews)</span>
        </div>
      </div>

      <div style={{ padding: '20px' }}>
        <h4 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: 800 }}>{prop.name}</h4>
        <p style={{ fontSize: '14px', color: 'var(--text-muted)', margin: '0 0 16px 0', height: '3em', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
          {prop.description}
        </p>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid var(--border)', paddingTop: '16px' }}>
          <div style={{ display: 'flex', gap: '12px', fontSize: '12px', color: 'var(--text-muted)', fontWeight: 600 }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><Navigation size={14} color="var(--secondary)" /> Explore</span>
          </div>
          <button className="btn btn-primary" style={{ padding: '8px 16px', borderRadius: '10px', fontSize: '13px' }}>Know More</button>
        </div>
      </div>
    </div>
  );
};

export default TouristDashboard;
